import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
} from '@mui/material';
import { createRoomDirect, updateRoomDirect } from '@timetable/api';
import { useAuthStore } from '../../store/authStore';
import { queryKeys } from '../../lib/queryKeys';
import type { Room, CreateRoomRequest, UpdateRoomRequest } from '@timetable/types';

interface RoomFormDialogProps {
  open: boolean;
  room: Room | null;
  onClose: () => void;
}

export function RoomFormDialog({ open, room, onClose }: RoomFormDialogProps) {
  const queryClient = useQueryClient();
  const schoolContext = useAuthStore((state) => state.schoolContext);
  const isEditMode = !!room;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [capacity, setCapacity] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (room) {
      setName(room.name);
      setDescription(room.description || '');
      setCapacity(room.capacity != null ? String(room.capacity) : '');
    } else {
      setName('');
      setDescription('');
      setCapacity('');
    }
    setValidationError(null);
  }, [room, open]);

  const createMutation = useMutation({
    mutationFn: (data: CreateRoomRequest) => createRoomDirect(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all });
      onClose();
    },
    onError: (error: Error) => {
      // Supabase unique constraint violation
      if (error.message?.includes('23505') || error.message?.toLowerCase().includes('unique')) {
        setValidationError('A room with this name already exists in this school.');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateRoomRequest) => updateRoomDirect(room!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all });
      onClose();
    },
    onError: (error: Error) => {
      if (error.message?.includes('23505') || error.message?.toLowerCase().includes('unique')) {
        setValidationError('A room with this name already exists in this school.');
      }
    },
  });

  const validate = (): boolean => {
    if (!name.trim()) {
      setValidationError('Room name is required');
      return false;
    }
    if (capacity && (isNaN(Number(capacity)) || Number(capacity) < 1)) {
      setValidationError('Capacity must be a positive number');
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const capacityValue = capacity ? Number(capacity) : undefined;

    if (isEditMode) {
      updateMutation.mutate({
        name: name.trim(),
        description: description.trim() || undefined,
        capacity: capacityValue,
      });
    } else {
      if (!schoolContext) {
        setValidationError('School context not found');
        return;
      }
      createMutation.mutate({
        school_id: schoolContext,
        name: name.trim(),
        description: description.trim() || undefined,
        capacity: capacityValue,
      });
    }
  };

  const mutation = isEditMode ? updateMutation : createMutation;
  const apiError = mutation.error && !validationError ? (mutation.error as Error).message : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Room' : 'Add Room'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {validationError && (
            <Alert severity="error" onClose={() => setValidationError(null)}>
              {validationError}
            </Alert>
          )}
          {apiError && (
            <Alert severity="error">{apiError}</Alert>
          )}

          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
            placeholder="e.g., Computer Lab, Swimming Pool"
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Optional description of the room"
          />

          <TextField
            label="Capacity"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            fullWidth
            type="number"
            inputProps={{ min: 1 }}
            placeholder="e.g., 30"
            helperText="Optional. Maximum number of students."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
